import FilterOption from './FilterOption'

export default class Filter {
  static empty() {
    return new Filter({
      hasAnyPreselectedFilterWithZeroMatches: false,
      options: []
    })
  }

  static availableOptionsFrom(availableStays, preselectedFilters, currentSelectedRoom) {
    const points = preselectedFilters[FilterOption.POINTS]
      ? FilterOption.points({ order: 1 }).select()
      : FilterOption.points({ order: 1 })
    const freeCancellation = preselectedFilters[FilterOption.FREE_CANCELLATION]
      ? FilterOption.freeCancellation({ order: 2 }).select()
      : FilterOption.freeCancellation({ order: 2 })
    const breakfast = preselectedFilters[FilterOption.BREAKFAST]
      ? FilterOption.breakfast({ order: 3 }).select()
      : FilterOption.breakfast({ order: 3 })
    const packages = preselectedFilters[FilterOption.PACKAGES]
      ? FilterOption.packages({ order: 4 }).select()
      : FilterOption.packages({ order: 4 })
    const accessibleRooms = preselectedFilters[FilterOption.ACCESSIBLE_ROOMS]
      ? FilterOption.accessibleRooms({ order: 5 }).select()
      : FilterOption.accessibleRooms({ order: 5 })
    const multiroomOption = preselectedFilters[FilterOption.MULTIROOM_ID]
      ? FilterOption.occupancyIds({
        roomId: currentSelectedRoom.roomId,
        mealPlan: currentSelectedRoom.mealPlan,
        rateType: currentSelectedRoom.rateType,
        order: 999
      }).select()
      : FilterOption.occupancyIds({
        roomId: '1',
        mealPlan: null,
        rateType: null,
        order: 999,
        selected: true
      })
    const optionalOptions = [points, freeCancellation, breakfast, packages]

    const availableOptions = optionalOptions.reduce(
      (accumulation, option) =>
        availableStays.filter(option.match).length > 0 ? [...accumulation, option] : accumulation,
      []
    )

    const hasAnyPreselectedFilterWithZeroMatches = optionalOptions
      .filter(option => option.selected)
      .reduce(
        (accumulation, option) => accumulation || availableStays.filter(option.match).length === 0,
        false
      )

    return new Filter({
      hasAnyPreselectedFilterWithZeroMatches,
      options: [...availableOptions, accessibleRooms, multiroomOption]
    })
  }

  constructor({ hasAnyPreselectedFilterWithZeroMatches, options }) {
    this.hasAnyPreselectedFilterWithZeroMatches = hasAnyPreselectedFilterWithZeroMatches
    this.options = options || []
  }

  clone() {
    return new Filter({
      ...this
    })
  }

  hasAnySelection(isFirstRoom) {
    if (isFirstRoom) {
      return (
        this.options.filter(option => {
          if (option.code === FilterOption.MULTIROOM_ID) return false
          return option.selected
        }).length > 0
      )
    } else {
      return (
        this.options.filter(option => {
          if (option.code === FilterOption.MULTIROOM_ID) return false
          if (option.code === FilterOption.BREAKFAST) return false
          return option.selected
        }).length > 0
      )
    }
  }

  recalculateCounters(matchesRoom) {
    const options = this.options.map(option => {
      const roomIds = matchesRoom[option.code]?.roomIds
      const matches = option.updateCounter(matchesRoom[option.code]?.matches || 0).matches
      return {
        ...option,
        matches: matches,
        roomIds: roomIds
      }
    })
    return new Filter({ ...this, options })
  }

  count(stay) {
    return this.options.reduce((accumulation, option) => {
      accumulation[option.code] = option.match(stay) ? 1 : 0
      return accumulation
    }, {})
  }

  match(stay) {
    return this.options
      .filter(option => option.selected)
      .reduce(
        (accumulation, option) => {
          const match = option.match(stay)
          accumulation.fullMatch = accumulation.fullMatch && match
          accumulation.matches += match ? 1 : 0
          return accumulation
        },
        { stay, fullMatch: true, matches: 0 }
      )
  }

  select(option) {
    return new Filter({
      ...this,
      options: this.options
        .filter(o => o.code !== option.code)
        .concat([option.select()])
        .sort((a, b) => a.order - b.order)
    })
  }

  unselect(option) {
    return new Filter({
      ...this,
      options: this.options
        .filter(o => o.code !== option.code)
        .concat([option.unselect()])
        .sort((a, b) => a.order - b.order)
    })
  }

  unselectPoints() {
    const option = this.options.find(o => o.code === FilterOption.POINTS)
    return option ? this.unselect(option) : this
  }

  reset(full) {
    return new Filter({
      ...this,
      options: this.options.map(option => {
        if (!full && option.code === FilterOption.MULTIROOM_ID) return option
        // if (option.code === FilterOption.BREAKFAST) return option
        return option.unselect()
      })
    })
  }

  selectOccupancyIds(roomId, mealPlan, rateType) {
    const option = this.options.find(o => o.code === FilterOption.MULTIROOM_ID)
    return new Filter({
      ...this,
      options: this.options
        .filter(o => o.code !== option?.code)
        .concat([
          FilterOption.occupancyIds({ roomId, mealPlan, rateType, order: 999, selected: true })
        ])
        .sort((a, b) => a.order - b.order)
    })
  }

  selection() {
    return {
      [FilterOption.POINTS]:
        this.options.filter(o => o.code === FilterOption.POINTS && o.selected).length === 1,
      [FilterOption.FREE_CANCELLATION]:
        this.options.filter(o => o.code === FilterOption.FREE_CANCELLATION && o.selected).length ===
        1,
      [FilterOption.BREAKFAST]:
        this.options.filter(o => o.code === FilterOption.BREAKFAST && o.selected).length === 1,
      [FilterOption.PACKAGES]:
        this.options.filter(o => o.code === FilterOption.PACKAGES && o.selected).length === 1,
      [FilterOption.ACCESSIBLE_ROOMS]:
        this.options.filter(o => o.code === FilterOption.ACCESSIBLE_ROOMS && o.selected).length ===
        1,
      [FilterOption.MULTIROOM_ID]:
        this.options.filter(o => o.code === FilterOption.MULTIROOM_ID && o.selected).length === 1
    }
  }
}
